import React, { useState } from 'react';
import { BRAND_COLORS } from '../globalTheme';

interface ToggleProps {
	defaultChecked?: boolean;
	onChange?: (checked: boolean) => void;
	disabled?: boolean;
	size?: 'small' | 'medium' | 'large';
	label?: string;
	id?: string;
}

const Toggle: React.FC<ToggleProps> = ({
	defaultChecked = false,
	onChange,
	disabled = false,
	size = 'medium',
	label,
	id
}) => {
	const [isChecked, setIsChecked] = useState(defaultChecked);

	const handleToggle = () => {
		if (disabled) return;

		const newChecked = !isChecked;
		setIsChecked(newChecked);
		onChange?.(newChecked);
	};

	const getSizeClasses = () => {
		switch (size) {
			case 'small':
				return 'w-8 h-4';
			case 'large':
				return 'w-14 h-7';
			default:
				return 'w-11 h-6';
		}
	};

	const getThumbSizeClasses = () => {
		switch (size) {
			case 'small':
				return 'w-3 h-3';
			case 'large':
				return 'w-5 h-5';
			default:
				return 'w-5 h-5';
		}
	};

	const getThumbTransform = () => {
		switch (size) {
			case 'small':
				return isChecked ? 'translateX(16px)' : 'translateX(2px)';
			case 'large':
				return isChecked ? 'translateX(28px)' : 'translateX(4px)';
			default:
				return isChecked ? 'translateX(20px)' : 'translateX(2px)';
		}
	};

	return (
		<div className="flex items-center gap-3">
			<button
				type="button"
				role="switch"
				aria-checked={isChecked}
				aria-labelledby={id}
				onClick={handleToggle}
				disabled={disabled}
				className={`
          relative inline-flex shrink-0 cursor-pointer rounded-full border-2 border-transparent 
          transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 
          focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50
          ${getSizeClasses()}
          ${isChecked ? `bg-[${BRAND_COLORS.brand600}]` : 'bg-gray-200'}
        `}
			>
				<span
					className={`
            pointer-events-none inline-block rounded-full bg-white shadow-lg transform 
            ring-0 transition duration-200 ease-in-out
            ${getThumbSizeClasses()}
          `}
					style={{
						transform: getThumbTransform()
					}}
				/>
			</button>

			{label && (
				<label
					id={id}
					className={`
            text-sm font-medium text-gray-900 cursor-pointer select-none
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
					onClick={!disabled ? handleToggle : undefined}
				>
					{label}
				</label>
			)}
		</div>
	);
};

export default Toggle;
